// Tabs/NotesTab.tsx
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Loader,
  Center,
  Alert,
  Stack,
  Box,
  Text,
  ScrollArea,
  Textarea,
  Button,
  Group,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconRefresh, IconSend } from '@tabler/icons-react';
import ChatBubble from '@/components/advanced/communication/ChatBubble';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useLeaderPortalService,
  useUserService,
  CareCaseNote,
} from '@actschurch/ccms-sdk';
import ConfirmActionModal from './ConfirmActionModal';

export interface NotesTabProps {
  caseId: number;
  active: boolean;
  currentUserId?: string;
}

const NOTE_EDIT_WINDOW_MINUTES = 10;

interface NoteItem {
  id: string;
  noteID: number;
  authorId: string;
  authorName: string;
  message: string;
  timestamp: string;
  isEdited: boolean;
  createdDateTime: number | string;
  entryDateTime: number | string;
}

function sdkDateToISO(value: number | string): string {
  const raw = String(value ?? '');
  if (raw.length < 14) return new Date().toISOString();
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}T${raw.slice(8, 10)}:${raw.slice(10, 12)}:${raw.slice(12, 14)}`;
}

// SDK yyyyMMddHHmmss → unix milliseconds
function parseSdkTimestamp(value: number | string): number {
  const raw = String(value ?? '');
  if (raw.length < 14) return Date.now();
  const year    = Number(raw.slice(0, 4));
  const month   = Number(raw.slice(4, 6)) - 1;
  const day     = Number(raw.slice(6, 8));
  const hours   = Number(raw.slice(8, 10));
  const minutes = Number(raw.slice(10, 12));
  const seconds = Number(raw.slice(12, 14));
  return new Date(year, month, day, hours, minutes, seconds).getTime();
}

// Whether a note timestamp is still within the edit/delete window.
function isWithinEditWindow(dateTimeValue: number | string): boolean {
  const noteTimestamp = parseSdkTimestamp(dateTimeValue);
  const windowMs = NOTE_EDIT_WINDOW_MINUTES * 60 * 1000;
  return Date.now() - noteTimestamp <= windowMs;
}

function decodeNote(value: string): string {
  try {
    return decodeURIComponent(escape(atob(value)));
  } catch {
    return value ?? '';
  }
}

function encodeNote(value: string): string {
  try {
    return btoa(unescape(encodeURIComponent(value)));
  } catch {
    return value;
  }
}

export default function NotesTab({ caseId, active, currentUserId }: NotesTabProps) {
  const leaderPortal = useLeaderPortalService();
  const userService  = useUserService();

  const [notes, setNotes]               = useState<NoteItem[] | null>(null);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [currentUserName, setCurrentUserName] = useState('You');
  const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null);
  const [deleteNoteOpen, setDeleteNoteOpen] = useState(false);
  const [deletingNote, setDeletingNote] = useState(false);

  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  const nameCache = useRef<Map<string, string>>(new Map());
  const noteIdsRef = useRef<Set<string>>(new Set());
  const notesMapRef = useRef<Map<string, CareCaseNote>>(new Map());

  const resolveName = useCallback(async (userId: string): Promise<string> => {
    if (!userId) return 'Leader';
    if (nameCache.current.has(userId)) return nameCache.current.get(userId)!;
    try {
      const res: any = await userService.getById(userId);
      const user = res?.data ?? res;
      if (user) {
        const name =
          [user.firstName, user.lastName ?? user.surname].filter(Boolean).join(' ').trim() ||
          user.displayName?.trim() ||
          user.email ||
          userId;
        nameCache.current.set(userId, name);
        return name;
      }
    } catch {
      // fall through
    }
    nameCache.current.set(userId, userId);
    return userId;
  }, [userService]);

  useEffect(() => {
    if (!currentUserId) {
      setCurrentUserName('You');
      return;
    }
    resolveName(currentUserId).then(setCurrentUserName);
  }, [currentUserId, resolveName]);

  const noteToItem = useCallback(async (note: CareCaseNote): Promise<NoteItem> => {
  const authorId = note.userId ?? note.adminUserId ?? '';
  let authorName = 'Unknown';
  if (note.userId) {
    authorName = await resolveName(note.userId);
  } else if (note.adminUserId) {
    authorName = `${await resolveName(note.adminUserId)}`;
  }
  return {
    id: String(note.noteID),
    noteID: note.noteID,
    authorId,
    authorName,
    message: decodeNote(note.note ?? ''),
    timestamp: sdkDateToISO(note.entryDateTime),
    isEdited: !!note.modifiedDateTime && note.modifiedDateTime !== note.createdDateTime,
    createdDateTime: note.createdDateTime,
    entryDateTime: note.entryDateTime,
  };
}, [resolveName]);

  const fetchNotes = useCallback(async () => {
    if (!caseId) return;
    setLoadingNotes(true);
    try {
      const res: any = await leaderPortal.caseNotes(caseId);
      const raw: CareCaseNote[] = res?.data ?? res ?? [];

      // Leaders never see notes flagged hideFromLeader — filter before anything else touches them.
      const visible = raw.filter((n) => !n.hideFromLeader);
      const sorted = [...visible].sort((a, b) => a.entryDateTime - b.entryDateTime);

      notesMapRef.current = new Map(sorted.map((n) => [String(n.noteID), n]));
      noteIdsRef.current = new Set(sorted.map((n) => String(n.noteID)));

      const resolved = await Promise.all(sorted.map(noteToItem));
      setNotes(resolved);
    } catch (e) {
      console.error('Error fetching case notes:', e);
      toast.error('Error', 'Could not load notes.');
      setNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  }, [leaderPortal, caseId, noteToItem]);

  useEffect(() => {
    if (active && notes === null) fetchNotes();
  }, [active, notes, fetchNotes]);

  const handlePost = async () => {
    const content = draft.trim();
    if (!content) return;
    setPosting(true);
    try {
      await leaderPortal.addCaseNote(caseId, {
        caseID: caseId,
        note:   encodeNote(content),
        // Leaders can't hide notes from themselves — always false here.
        hideFromLeader: false,
      } as any);
      setDraft('');
      await fetchNotes();
      toast.success('Note Added', 'Your note has been saved.');
    } catch (e) {
      console.error('Error adding case note:', e);
      toast.error('Error', 'Could not save note. Please try again.');
    } finally {
      setPosting(false);
    }
  };

  const startEdit = (item: NoteItem) => {
    const note = notesMapRef.current.get(item.id);
    if (note && !isWithinEditWindow(note.createdDateTime ?? note.entryDateTime)) {
      toast.error('Action Not Allowed', `Notes cannot be edited after ${NOTE_EDIT_WINDOW_MINUTES} minutes from creation.`);
      return;
    }
    setEditingId(item.id);
    setEditDraft(item.message);
  };

  const handleSaveEdit = async (item: NoteItem) => {
    const content = editDraft.trim();
    if (!content) return;

    const note = notesMapRef.current.get(item.id);
    if (note && !isWithinEditWindow(note.createdDateTime ?? note.entryDateTime)) {
      toast.error('Action Not Allowed', `Notes cannot be edited after ${NOTE_EDIT_WINDOW_MINUTES} minutes from creation.`);
      setEditingId(null);
      return;
    }

    const noteId = Number(item.id);
    if (!noteId) return;

    try {
      await leaderPortal.updateCaseNote(caseId, noteId, { note: encodeNote(content) } as any);
      setEditingId(null);
      setEditDraft('');
      await fetchNotes();
      toast.success('Note Updated', 'Your note has been updated.');
    } catch (e) {
      console.error('Error updating case note:', e);
      toast.error('Error', 'Could not update note. Please try again.');
    }
  };

  const handleDeleteComment = (id: string) => {
    const note = notesMapRef.current.get(id);
    if (note && !isWithinEditWindow(note.createdDateTime ?? note.entryDateTime)) {
      toast.error('Action Not Allowed', `Notes cannot be deleted after ${NOTE_EDIT_WINDOW_MINUTES} minutes from creation.`);
      return;
    }
    setDeleteNoteId(id);
    setDeleteNoteOpen(true);
  };

  const handleConfirmDeleteComment = async () => {
    const id = deleteNoteId;
    if (!id) {
      setDeleteNoteOpen(false);
      return;
    }

    const noteId = Number(id);
    if (!noteId) {
      setDeleteNoteOpen(false);
      setDeleteNoteId(null);
      return;
    }

    const note = notesMapRef.current.get(id);
    if (note && !isWithinEditWindow(note.createdDateTime ?? note.entryDateTime)) {
      toast.error('Action Not Allowed', `${NOTE_EDIT_WINDOW_MINUTES}-minute window for deletion has passed.`);
      setDeleteNoteOpen(false);
      setDeleteNoteId(null);
      return;
    }

    setDeletingNote(true);
    try {
      const ok = await leaderPortal.deleteCaseNote(caseId, noteId);
      if (ok) {
        toast.warning('Note Deleted', 'The note has been deleted.');
        await fetchNotes();
      } else {
        toast.error('Error', 'Could not delete note.');
      }
    } catch (e) {
      console.error('Error deleting case note:', e);
      toast.error('Error', 'Could not delete note. Please try again.');
    } finally {
      setDeletingNote(false);
      setDeleteNoteOpen(false);
      setDeleteNoteId(null);
    }
  };

  if (notes === null || (loadingNotes && notes.length === 0)) {
    return (
      <Center py="md">
        <Loader size="sm" />
      </Center>
    );
  }

  return (
    <>
      <Alert
        variant="light"
        color="yellow"
        title="Notice"
        icon={<IconAlertCircle size={18} />}
        mb="sm"
      >
        Notes can only be edited or deleted within {NOTE_EDIT_WINDOW_MINUTES} minutes of creation.
      </Alert>

      <Group justify="right" mb="md">
        <Tooltip label="Refresh notes" withArrow>
          <ActionIcon variant="subtle" onClick={fetchNotes} loading={loadingNotes} size="lg">
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <Stack gap="md">
        <ScrollArea.Autosize mah={480} type="auto">
          <Stack gap={4} py="xs">
            {notes.length === 0 && (
              <Center py="lg">
                <Text size="sm" c="dimmed">No notes yet.</Text>
              </Center>
            )}
            {notes.map((item) => {
              const isOwn = !!currentUserId && item.authorId === currentUserId;

              if (editingId === item.id) {
                return (
                  <Box key={item.id} maw="70%" ml={isOwn ? 'auto' : 0} mr={isOwn ? 0 : 'auto'}>
                    <Textarea
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.currentTarget.value)}
                      autosize
                      minRows={2}
                    />
                    <Group gap="xs" justify="flex-end" mt={4}>
                      <Button size="xs" variant="subtle" onClick={() => setEditingId(null)}>
                        Cancel
                      </Button>
                      <Button size="xs" onClick={() => handleSaveEdit(item)}>
                        Save
                      </Button>
                    </Group>
                  </Box>
                );
              }

              return (
                <Box key={item.id} maw="70%" ml={isOwn ? 'auto' : 0} mr={isOwn ? 0 : 'auto'}>
                  <ChatBubble
                    message={item.message}
                    sender={{ name: item.authorName }}
                    timestamp={item.timestamp}
                    isOwn={isOwn}
                    isEdited={item.isEdited}
                    showAvatar={true}
                    onEdit={isOwn ? () => startEdit(item) : undefined}
                    onDelete={isOwn ? () => handleDeleteComment(item.id) : undefined}
                    onCopy={() => {
                      navigator.clipboard.writeText(item.message);
                      toast.success('Copied', 'Note copied to clipboard.');
                    }}
                  />
                </Box>
              );
            })}
          </Stack>
        </ScrollArea.Autosize>

        <Stack gap="xs">
          <Textarea
            placeholder="Write a note…"
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            autosize
            minRows={2}
            maxRows={6}
          />
          <Group justify="flex-end">
            <Button
              leftSection={<IconSend size={14} />}
              onClick={handlePost}
              loading={posting}
              disabled={!draft.trim()}
            >
              Post
            </Button>
          </Group>
        </Stack>
      </Stack>

      <ConfirmActionModal
        opened={deleteNoteOpen}
        onClose={() => setDeleteNoteOpen(false)}
        title="Delete note"
        message="Delete this note? This cannot be undone."
        color="red"
        onConfirm={handleConfirmDeleteComment}
      />
    </>
  );
}